import useSSE from '../useSSE';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import api from '../utils/api';

function Avatar({ name, color, size=26 }) {
  const i = (name||'?').split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase();
  return <div style={{width:size,height:size,borderRadius:'50%',background:color||'#5B5BD6',display:'flex',alignItems:'center',justifyContent:'center',fontSize:size*.34,fontWeight:'700',color:'#fff',flexShrink:0}}>{i}</div>;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d+'T00:00:00').toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'});
}
function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'});
}

const STATUS_CONFIG = {
  pending:          { label:'Pending',           color:'#6B7280', bg:'#F3F4F6' },
  ready_for_release:{ label:'Ready for Release', color:'#059669', bg:'#D1FAE5' },
  released:         { label:'Released',           color:'#1D4ED8', bg:'#DBEAFE' },
  cancelled:        { label:'Cancelled',          color:'#DC2626', bg:'#FEE2E2' },
};

// ── Multi-select for Finance Responsible ────────────────────────────────
function FinanceMultiSelect({ users, selected, onChange }) {
  const [search, setSearch] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const filtered = users.filter(u => u.full_name.toLowerCase().includes(search.toLowerCase()));
  const selectedUsers = users.filter(u => selected.includes(u.id));
  return (
    <div ref={ref} style={{position:'relative'}}>
      <div onClick={() => setOpen(o => !o)}
        style={{border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'7px 10px',cursor:'pointer',
          background:'var(--surface)',minHeight:'38px',display:'flex',flexWrap:'wrap',gap:'4px',alignItems:'center'}}>
        {selectedUsers.length === 0
          ? <span style={{color:'var(--muted)',fontSize:'13px'}}>— Select finance responsible —</span>
          : selectedUsers.map(u => (
            <span key={u.id} style={{background:'rgba(91,91,214,0.12)',color:'#5B5BD6',borderRadius:'99px',
              padding:'2px 8px',fontSize:'11px',fontWeight:'600',display:'flex',alignItems:'center',gap:'4px'}}>
              {u.full_name}
              <span onClick={e=>{e.stopPropagation();onChange(selected.filter(id=>id!==u.id));}}
                style={{cursor:'pointer',fontWeight:'900',opacity:0.6}}>×</span>
            </span>
          ))
        }
        <span style={{marginLeft:'auto',color:'var(--muted)',fontSize:'11px'}}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,zIndex:200,
          background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius)',
          boxShadow:'0 4px 16px rgba(0,0,0,0.12)',maxHeight:'220px',overflow:'hidden',display:'flex',flexDirection:'column'}}>
          <input autoFocus value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search users…" style={{margin:'8px',padding:'6px 10px',borderRadius:'6px',
              border:'1px solid var(--border)',fontSize:'12px',outline:'none'}}/>
          <div style={{overflowY:'auto',flex:1}}>
            {filtered.map(u => {
              const checked = selected.includes(u.id);
              return (
                <label key={u.id} style={{display:'flex',alignItems:'center',gap:'8px',padding:'7px 12px',
                  cursor:'pointer',background:checked?'rgba(91,91,214,0.06)':'transparent',
                  borderBottom:'1px solid var(--border)'}}>
                  <input type="checkbox" checked={checked}
                    onChange={() => onChange(checked ? selected.filter(id=>id!==u.id) : [...selected, u.id])}/>
                  <span style={{fontSize:'12px',color:'var(--text)',fontWeight:checked?'600':'400'}}>{u.full_name}</span>
                </label>
              );
            })}
            {filtered.length === 0 && <div style={{padding:'12px',fontSize:'12px',color:'var(--muted)',textAlign:'center'}}>No users found</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PaymentMilestonesPage() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const backToDash = () => navigate(spaceId ? `/spaces/${spaceId}` : '/spaces');

  // Real-time SSE
  useSSE(spaceId, {
    payment_updated: () => { silentRef.current=true; load().finally(()=>{ silentRef.current=false; }); },
    task_updated: () => { silentRef.current=true; load().finally(()=>{ silentRef.current=false; }); },
  });

  // Reload when phase access changes
  React.useEffect(() => {
    function onAccessChanged() {
      // Re-trigger the data load by updating a counter state
      setReloadKey(k => k + 1);
    }
    window.addEventListener('trajectory:access_changed', onAccessChanged);
    return () => window.removeEventListener('trajectory:access_changed', onAccessChanged);
  }, [spaceId]);
  const ctx = useOutletContext();
  const user = ctx?.user || {};
  const isGuest = ctx?.isGuest || user?.role === 'guest';
  const [isProjectPM, setIsProjectPM] = useState(user?.role === 'superadmin');

  const [reloadKey, setReloadKey] = useState(0);
  const [milestones, setMilestones] = useState([]);
  const [allTasks,   setAllTasks]   = useState([]);
  const [allUsers,   setAllUsers]   = useState([]);
  const [space,      setSpace]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const silentRef = React.useRef(false);

  const [expanded,   setExpanded]   = useState({});
  const [showModal,  setShowModal]  = useState(false);
  const [editPm,     setEditPm]     = useState(null);
  const [releaseModal, setReleaseModal] = useState(null);
  const [deleteId,   setDeleteId]   = useState(null);
  const [toast,      setToast]      = useState(null);

  const emptyForm = { name:'', description:'', amount:'', currency:'OMR',
    planned_date:'', finance_user_id:'', contract_reference:'', task_ids:[] };
  const [form, setForm] = useState(emptyForm);
  const [releaseForm, setReleaseForm] = useState({ reference_number:'', notes:'', amount:'' });

  const showToast = (msg, type='success') => {
    setToast({msg,type});
    setTimeout(()=>setToast(null),3500);
  };

  const load = useCallback(async () => {
    if(!silentRef.current) setLoading(true);
    try {
      // Check if current user is a designated PM for this project
      const pmCheck = await api.get(`/projects/${spaceId}/pms`).catch(()=>[]);
      const isPM = user?.role === 'superadmin' ||
                   (Array.isArray(pmCheck) && pmCheck.some(p => p.user_id === user?.id));
      setIsProjectPM(isPM);

      if (!isPM && user?.role !== 'superadmin') {
        setLoading(false);
        return; // No access - page will show access denied
      }

      const [sp, pms, tasks, users] = await Promise.allSettled([
        api.get(`/spaces/${spaceId}`),
        api.get(`/spaces/${spaceId}/payment-milestones`),
        api.get(`/spaces/${spaceId}/tasks-list`),
        api.get('/users'),
      ]);
      if (sp.status==='fulfilled') setSpace(sp.value);
      setMilestones(pms.status==='fulfilled' ? (pms.value||[]) : []);
      setAllTasks(tasks.status==='fulfilled' ? (tasks.value||[]) : []);
      setAllUsers(users.status==='fulfilled' ? (users.value||[]) : []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [spaceId, user?.id, user?.role]);

  useEffect(()=>{ load(); },[load]);
  // ── Silent background refresh ─────────────────────────────────────────────
  useEffect(()=>{
    let lastTs=null, active=true, fails=0;
    const poll=async()=>{
      if(!active||!spaceId) return;
      try {
        const r=await api.get(`/spaces/${spaceId}/last-updated`).catch(()=>null);
        if(!r){fails++;if(fails>4)active=false;return;}
        fails=0;
        if(lastTs===null){lastTs=r.last_updated;return;}
        if(r.last_updated!==lastTs){lastTs=r.last_updated;silentRef.current=true;load().finally(()=>{silentRef.current=false;});}
      } catch(_){fails++;}
    };
    const iv=setInterval(poll,15000);
    return()=>{active=false;clearInterval(iv);};
  },[spaceId]);

  function openNew() {
    setForm(emptyForm);
    setEditPm(null);
    setShowModal(true);
  }

  function openEdit(pm) {
    setForm({
      name: pm.name||'', description: pm.description||'',
      amount: String(pm.amount||''), currency: pm.currency||'OMR',
      planned_date: pm.planned_date||'', finance_user_id: pm.finance_user_id||'',
      contract_reference: pm.contract_reference||'',
      task_ids: pm.tasks?.map(t=>t.id)||[],
    });
    setEditPm(pm);
    setShowModal(true);
  }

  async function saveMilestone() {
    if (!form.name.trim()) return showToast('Name is required','error');
    if (!form.amount || parseFloat(form.amount)<=0) return showToast('Amount must be greater than 0','error');
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (editPm) {
        await api.put(`/payment-milestones/${editPm.id}`, payload);
        showToast('Milestone updated');
      } else {
        await api.post(`/spaces/${spaceId}/payment-milestones`, payload);
        showToast('Payment milestone created');
      }
      setShowModal(false);
      load();
    } catch(e) { showToast(e?.error||'Failed to save','error'); }
  }

  async function releasePayment(keepOpen=false) {
    try {
      const releaseAmt = parseFloat(releaseForm.amount)||releaseModal.amount;
      await api.post(`/payment-milestones/${releaseModal.id}/release`, {
        ...releaseForm,
        amount: releaseAmt,
        keep_open: keepOpen,
      });
      showToast('Payment released successfully 💰');
      setReleaseModal(null);
      load();
    } catch(e) { showToast(e?.error||'Failed to release','error'); }
  }

  async function deleteMilestone() {
    try {
      await api.delete(`/payment-milestones/${deleteId}`);
      setDeleteId(null);
      showToast('Milestone deleted');
      load();
    } catch(e) { showToast(e?.error||'Failed','error'); }
  }

  function toggleTask(id) {
    setForm(f => ({
      ...f,
      task_ids: f.task_ids.includes(id) ? f.task_ids.filter(x=>x!==id) : [...f.task_ids, id]
    }));
  }

  // Summary stats
  const totalContract  = milestones.reduce((s,m)=>s+parseFloat(m.amount||0),0);
  // Total Released = sum of actual release log entries (not planned milestone amounts)
  const totalReleased  = milestones.reduce((s,m)=>{
    const actualReleased = (m.releaseLog||[]).reduce((rs,r)=>rs+parseFloat(r.amount||0),0);
    return s + (actualReleased > 0 ? actualReleased : (m.status==='released' ? parseFloat(m.amount||0) : 0));
  }, 0);
  // Multi-currency grouping — released uses ACTUAL release-log amounts
  const byCurrency = {};
  milestones.forEach(m=>{
    const cur = m.currency||'OMR';
    if(!byCurrency[cur]) byCurrency[cur]={total:0,released:0,pending:0,count:0};
    const amt = parseFloat(m.amount||0);
    const actualReleased = (m.releaseLog||[]).reduce((rs,r)=>rs+parseFloat(r.amount||0),0);
    const rel = actualReleased > 0 ? actualReleased : (m.status==='released' ? amt : 0);
    byCurrency[cur].total += amt;
    byCurrency[cur].count++;
    byCurrency[cur].released += rel;
    if(m.status!=='released' && m.status!=='cancelled') byCurrency[cur].pending += amt;
  });
  const currencies = Object.keys(byCurrency);
  const isMultiCurrency = currencies.length > 1;
  const totalPending   = totalContract - totalReleased;
  const readyCount     = milestones.filter(m=>m.status==='ready_for_release').length;
  // Over-release is judged on the PROJECT TOTAL per currency, not per milestone.
  // An overpaid milestone and an underpaid one cancel out, so the project only
  // shows an over-release if the TOTAL released exceeds the TOTAL contract value.
  const totalOverRelease = currencies.reduce((sum, c) => {
    const over = byCurrency[c].released - byCurrency[c].total;
    return sum + (over > 0 ? over : 0);
  }, 0);
  const currency = milestones[0]?.currency||'OMR';

  // Group tasks by phase for form
  const phases = [...new Set(allTasks.map(t=>t.phase_name||'Other'))];


  function fmtStatus(raw) {
    if (!raw) return '';
    const labels = {
      pending: 'Pending',
      ready_for_release: 'Ready for Payment',
      released: 'Paid / Released',
      cancelled: 'Cancelled',
      under_review: '⚠️ Under Review',
    };
    return labels[raw] || raw.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase());
  }

  function exportPaymentPrint() {
    try {
    const esc = s => String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const fmtAmt = n => (parseFloat(n||0)).toLocaleString('en-GB',{minimumFractionDigits:2,maximumFractionDigits:2});
    const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—';
    // cur = primary currency (first milestone's currency, or OMR)
    const cur = (milestones[0]?.currency) || 'OMR';
    const totalContract = milestones.reduce((s,m)=>s+parseFloat(m.amount||0),0);
    const totalReleased = milestones.reduce((s,m)=>{
    const ar=(m.releaseLog||[]).reduce((rs,r)=>rs+parseFloat(r.amount||0),0);
    return s+(ar>0?ar:(m.status==='released'?parseFloat(m.amount||0):0));
  },0);
    const totalPending = totalContract - totalReleased;
    // Per-currency grouping for multi-currency support
    const byCurrency = {};
    milestones.forEach(m=>{ const c=m.currency||'OMR'; if(!byCurrency[c]) byCurrency[c]={total:0,released:0,pending:0}; const a=parseFloat(m.amount||0); byCurrency[c].total+=a; const ar2=(m.releaseLog||[]).reduce((s,r)=>s+parseFloat(r.amount||0),0); byCurrency[c].released+=(ar2>0?ar2:(m.status==='released'?a:0)); if(m.status!=='released'&&m.status!=='cancelled') byCurrency[c].pending+=a; });

    const STATUS_COLOR = {
      pending:'#64748B', ready_for_release:'#D97706',
      released:'#16A34A', cancelled:'#DC2626', under_review:'#B45309'
    };
    const STATUS_BG = {
      pending:'#F1F5F9', ready_for_release:'#FEF9C3',
      released:'#DCFCE7', cancelled:'#FEE2E2', under_review:'#FEF3C7'
    };

    let rows = '';
    milestones.forEach((pm,i) => {
      const amt = parseFloat(pm.amount||0);
      const actualReleased = (pm.releaseLog||[]).reduce((s,r)=>s+parseFloat(r.amount||0),0);
      const variance = actualReleased - amt;
      const statusLbl = (pm.status||'pending').replace(/_/g,' ').toUpperCase();
      const sc = STATUS_COLOR[pm.status]||'#64748B';
      const sb = STATUS_BG[pm.status]||'#F1F5F9';
      const linkedTasks = (pm.tasks||[]).map(t=>esc(t.name)).join(', ')||'—';
      rows += `
        <tr style="background:${i%2===0?'#FFFFFF':'#F8FAFC'}">
          <td style="padding:10px 12px;border-bottom:1px solid #E2E8F0;font-weight:600;color:#1E293B;font-size:12px">${esc(pm.name)}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #E2E8F0;text-align:center">
            <span style="background:${sb};color:${sc};padding:2px 8px;border-radius:20px;font-size:10px;font-weight:700;border:1px solid ${sc}40">${statusLbl}</span>
          </td>
          <td style="padding:10px 12px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:700;color:#1E293B;font-size:12px">${cur} ${fmtAmt(amt)}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #E2E8F0;text-align:center;color:#64748B;font-size:11px">${fmtDate(pm.planned_date)}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #E2E8F0;text-align:center;color:${pm.released_at?'#16A34A':'#64748B'};font-size:11px">${pm.released_at?fmtDate(pm.released_at):'—'}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:11px;color:${actualReleased>0?'#16A34A':'#64748B'}">${actualReleased>0?cur+' '+fmtAmt(actualReleased):'—'}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #E2E8F0;font-size:10px;color:#64748B;max-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${esc(linkedTasks)}">${esc(linkedTasks.slice(0,60)+(linkedTasks.length>60?'…':''))}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #E2E8F0;text-align:right;font-size:11px;color:${variance>0?'#D97706':variance<0?'#DC2626':'#16A34A'};font-weight:${variance!==0?'700':'400'}">${variance!==0?(variance>0?'▲ +':' ')+cur+' '+fmtAmt(Math.abs(variance)):'—'}</td>
        </tr>`;
    });

    const dateStr = new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'});

    const html = `<!DOCTYPE html><html><head>
      <title>Payment Milestones — ${esc(space?.name||'Project')}</title>
      <meta charset="utf-8"/>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Segoe UI',Arial,sans-serif;background:#F8FAFC;color:#1E293B;padding:30px}
        @media print{
          @page{size:A3 landscape;margin:10mm}
          .no-print{display:none!important}
          body{padding:8px;background:white;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          .page-card{box-shadow:none!important;border:1px solid #E2E8F0!important;padding:20px!important}
          /* Force background colors to print */
          .kpi{-webkit-print-color-adjust:exact;print-color-adjust:exact}
          table td,table th{-webkit-print-color-adjust:exact;print-color-adjust:exact}
          span[style*="background"]{-webkit-print-color-adjust:exact;print-color-adjust:exact}
        }
        .no-print{display:flex;gap:8px;margin-bottom:20px;justify-content:flex-end}
        .btn-print{padding:9px 22px;background:#5B5BD6;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:700}
        .btn-close{padding:9px 14px;background:#E2E8F0;border:none;border-radius:6px;cursor:pointer;font-size:13px}
        .page-card{background:white;border-radius:12px;padding:36px;box-shadow:0 2px 24px rgba(0,0,0,.07);max-width:1200px;margin:0 auto}
        .report-header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:24px;border-bottom:2px solid #E2E8F0;margin-bottom:28px}
        .report-title{font-size:22px;font-weight:800;color:#1E293B}
        .report-meta{font-size:12px;color:#64748B;margin-top:6px}
        .kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:28px}
        .kpi{padding:16px 20px;border-radius:8px;border:1px solid #E2E8F0}
        .kpi .lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:#64748B;margin-bottom:6px}
        .kpi .val{font-size:20px;font-weight:800}
        table{width:100%;border-collapse:collapse}
        thead th{background:#1E293B;color:white;padding:11px 12px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px}
        thead th:last-child,thead th:nth-child(3),thead th:nth-child(6),thead th:nth-child(8){text-align:right}
        thead th:nth-child(2),thead th:nth-child(4),thead th:nth-child(5){text-align:center}
        .footer{margin-top:28px;padding-top:16px;border-top:1px solid #E2E8F0;display:flex;justify-content:space-between;align-items:center}
        .footer-brand{font-size:11px;color:#94A3B8;font-weight:600}
      </style>
      </head><body>
      <div class="no-print">
        <button class="btn-print" onclick="window.print()">🖨️ Print / Save as PDF</button>
        <button class="btn-close" onclick="window.close()">✕ Close</button>
      </div>
      <div class="page-card">
        <div class="report-header">
          <div>
            <div class="report-title">💰 Payment Milestones Report</div>
            <div class="report-meta">Project: <strong>${esc(space?.name||'—')}</strong> &nbsp;·&nbsp; ${esc(dateStr)}</div>
          </div>
          <div style="text-align:right">
            <div style="font-size:11px;color:#64748B">${milestones.length} milestone${milestones.length!==1?'s':''}</div>
          </div>
        </div>

        <div class="kpi-grid">
          <div class="kpi" style="border-color:#5B5BD640;background:#F0F0FF">
            <div class="lbl">Total Contract</div>
            ${Object.keys(byCurrency).map(c=>`<div class="val" style="color:#5B5BD6;font-size:${Object.keys(byCurrency).length>1?'14px':'20px'}">${c} ${fmtAmt(byCurrency[c].total)}</div>`).join('')}
          </div>
          <div class="kpi" style="border-color:#16A34A40;background:#F0FFF4">
            <div class="lbl">Total Released</div>
            ${Object.keys(byCurrency).map(c=>`<div class="val" style="color:#16A34A;font-size:${Object.keys(byCurrency).length>1?'14px':'20px'}">${c} ${fmtAmt(byCurrency[c].released)}</div>`).join('')}
          </div>
          <div class="kpi" style="border-color:#D9780640;background:#FFFBEB">
            <div class="lbl">Pending Release</div>
            ${Object.keys(byCurrency).map(c=>`<div class="val" style="color:#D97706;font-size:${Object.keys(byCurrency).length>1?'14px':'20px'}">${c} ${fmtAmt(byCurrency[c].pending)}</div>`).join('')}
          </div>
          <div class="kpi" style="border-color:#E2E8F0;background:#F8FAFC">
            <div class="lbl">Completion</div>
            <div class="val" style="color:#1E293B">${milestones.length>0?Math.round(milestones.filter(m=>m.status==='released').length/milestones.length*100):0}%</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width:180px">Milestone</th>
              <th style="width:120px;text-align:center">Status</th>
              <th style="width:110px;text-align:right">Amount</th>
              <th style="width:100px;text-align:center">Target Payment</th>
              <th style="width:100px;text-align:center">Released</th>
              <th style="width:110px;text-align:right">Actual Released</th>
              <th>Linked Tasks</th>
              <th style="width:100px;text-align:right">Variance</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>

        <div class="footer">
          <div class="footer-brand">Space Communication Technologies · Confidential</div>
          <div style="font-size:11px;color:#94A3B8">Enterprise Operations Management Platform (EOMP)</div>
        </div>
      </div>
      </body></html>`;

    // Use print iframe instead of popup (avoids popup blocker)
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;border:none;z-index:99999;background:white';
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(html);
    iframe.contentDocument.close();
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 2000);
    }, 600);
    } catch(err) { console.error('Print error:', err); showToast('Print failed: ' + err.message, 'error'); }
  }


  async function exportPaymentCSV() {
    const { downloadStyledWorkbook, prettyLabel } = await import('../utils/excelExport');
    try {
      const cur = milestones[0]?.currency||'OMR';
      const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '';
      const fmtAmt = n => parseFloat(n||0);

      // ── Sheet 1: Milestones Detail ────────────────────────────────────────
      const detailCols = [
        { header:'Milestone Name',            key:'name',     width:30 },
        { header:'Status',                    key:'status',   width:18, align:'center' },
        { header:`Contract Amount (${cur})`,  key:'amount',   width:18, align:'right', numFmt:'#,##0.00' },
        { header:'Target Payment Date',       key:'planned',  width:16, align:'center' },
        { header:'Actual Payment Date',       key:'released', width:16, align:'center' },
        { header:`Actual Released (${cur})`,  key:'actual',   width:18, align:'right', numFmt:'#,##0.00' },
        { header:`Over-Release (${cur})`,     key:'over',     width:16, align:'right', numFmt:'#,##0.00' },
        { header:'Tasks Total',               key:'tt',       width:11, align:'center' },
        { header:'Tasks Done',                key:'td',       width:11, align:'center' },
        { header:'% Complete',                key:'pct',      width:11, align:'center', numFmt:'0' },
        { header:'Finance Person',            key:'finance',  width:22 },
        { header:'Contract Ref',              key:'ref',      width:18 },
        { header:'Notes',                     key:'notes',    width:30 },
      ];
      const detailRows = milestones.map(m => {
        const actualReleased = (m.releaseLog||[]).reduce((s,r)=>s+fmtAmt(r.amount),0);
        const overRelease = Math.max(0, actualReleased - fmtAmt(m.amount));
        const tasksDone = (m.tasks||[]).filter(t=>parseFloat(t.progress||0)>=100).length;
        const tasksTotal = (m.tasks||[]).length;
        const pct = tasksTotal > 0 ? Math.round(tasksDone/tasksTotal*100) : 0;
        return {
          name: m.name||'',
          status: prettyLabel(m.status||'pending'),
          amount: fmtAmt(m.amount),
          planned: fmtD(m.planned_date),
          released: fmtD(m.released_at),
          actual: actualReleased > 0 ? actualReleased : '',
          over: overRelease > 0 ? overRelease : '',
          tt: tasksTotal,
          td: tasksDone,
          pct,
          finance: m.finance_user_name||'',
          ref: m.contract_reference||'',
          notes: m.release_notes||'',
        };
      });
      // Per-currency totals
      const exByCur = {};
      milestones.forEach(m=>{
        const c=m.currency||'OMR';
        exByCur[c] = exByCur[c]||{total:0,released:0};
        exByCur[c].total += fmtAmt(m.amount);
        const arE=(m.releaseLog||[]).reduce((s,r)=>s+fmtAmt(r.amount),0);
        exByCur[c].released += (arE>0?arE:(m.status==='released'?fmtAmt(m.amount):0));
      });
      const totalRow = (() => {
        const c = Object.keys(exByCur)[0];
        if (!c) return null;
        return { name:`TOTAL (${c})`, amount:exByCur[c].total, actual:exByCur[c].released,
                 over:Math.max(0,exByCur[c].released-exByCur[c].total) };
      })();

      // ── Sheet 2: Release Log ──────────────────────────────────────────────
      const logCols = [
        { header:'Milestone',        key:'name',  width:30 },
        { header:'Release Date',     key:'date',  width:14, align:'center' },
        { header:`Amount (${cur})`,  key:'amount',width:16, align:'right', numFmt:'#,##0.00' },
        { header:'Released By',      key:'by',    width:22 },
        { header:'Reference',        key:'ref',   width:18 },
        { header:'Notes',            key:'notes', width:30 },
      ];
      const logRows = [];
      milestones.forEach(m => {
        (m.releaseLog||[]).forEach(rl => {
          logRows.push({ name:m.name||'', date:fmtD(rl.released_at), amount:fmtAmt(rl.amount),
                         by:rl.released_by_name||'', ref:rl.reference_number||'', notes:rl.notes||'' });
        });
      });
      if (logRows.length === 0) logRows.push({ name:'No release records yet' });

      const safeName = (space?.name||'Payment').replace(/[^a-z0-9]/gi,'_').slice(0,30);
      await downloadStyledWorkbook(`Payment_Milestones_${safeName}_${new Date().toISOString().slice(0,10)}.xlsx`, [
        {
          name:'Milestones',
          title:`Payment Milestones — ${space?.name||''}`,
          subtitle:`Generated ${new Date().toLocaleString('en-GB')} · ${milestones.length} milestone${milestones.length!==1?'s':''}`,
          columns: detailCols,
          rows: detailRows,
          totalRow,
        },
        { name:'Release Log', columns: logCols, rows: logRows },
      ]);
    } catch(e) {
      console.error('Excel export error:', e);
      showToast('Excel export failed. Please try again.', 'error');
    }
  }

  function exportPaymentSVG() {
    const rowH=32, hdrH=40, pad=20;
    const cols=[{label:'Milestone',w:200},{label:'Amount',w:120},{label:'Status',w:110},{label:'Target Date',w:100},{label:'Released',w:90},{label:'Finance',w:150}];
    const totalW=cols.reduce((s,c)=>s+c.w,0)+pad*2;
    const totalH=hdrH+milestones.length*rowH+pad*2+60;
    let svg=`<svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="${totalH}">`;
    svg+=`<rect width="${totalW}" height="${totalH}" fill="#F8F9FC"/>`;
    const escName=s=>String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    svg+=`<text x="${pad}" y="${pad+16}" font-family="Arial" font-size="14" font-weight="bold" fill="#1e1e2e">Payment Milestones — ${escName(space?.name||'')}</text>`;
    svg+=`<text x="${pad}" y="${pad+30}" font-family="Arial" font-size="10" fill="#888">Exported: ${new Date().toLocaleDateString('en-GB')}</text>`;
    const totalContract=milestones.reduce((s,m)=>s+m.amount,0);
    const totalReleased=milestones.reduce((s,m)=>{
      const ar=(m.releaseLog||[]).reduce((rs,r)=>rs+parseFloat(r.amount||0),0);
      return s+(ar>0?ar:(m.status==='released'?parseFloat(m.amount||0):0));
    },0);
    // Build per-currency summary for SVG header
    const svgByCur = {};
    milestones.forEach(m=>{ const c=m.currency||'OMR'; if(!svgByCur[c]) svgByCur[c]={total:0,released:0}; const a=parseFloat(m.amount||0); svgByCur[c].total+=a; if(m.status==='released'||true) { const arS=(m.releaseLog||[]).reduce((s,r)=>s+parseFloat(r.amount||0),0); svgByCur[c].released+=(arS>0?arS:(m.status==='released'?a:0)); } });
    const svgCurLine = Object.keys(svgByCur).map(c=>`${c}: ${svgByCur[c].total.toLocaleString()} total | ${svgByCur[c].released.toLocaleString()} released`).join('   ');
    svg+=`<text x="${pad}" y="${pad+44}" font-family="Arial" font-size="10" fill="#059669" font-weight="bold">${svgCurLine}</text>`;
    let y=pad+50;
    svg+=`<rect x="${pad}" y="${y}" width="${totalW-pad*2}" height="${hdrH}" fill="#1e1e2e" rx="4"/>`;
    let x=pad;
    cols.forEach(col=>{
      svg+=`<text x="${x+8}" y="${y+24}" font-family="Arial" font-size="11" font-weight="bold" fill="white">${col.label}</text>`;
      x+=col.w;
    });
    y+=hdrH;
    milestones.forEach((pm,i)=>{
      const statusColor=pm.status==='released'?'#16A34A':pm.status==='ready_for_release'?'#059669':'#6B7280';
      svg+=`<rect x="${pad}" y="${y}" width="${totalW-pad*2}" height="${rowH}" fill="${i%2===0?'white':'#F3F4F6'}"/>`;
      x=pad;
      const esc=s=>String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&apos;');
      const vals=[esc(pm.name),esc(`${pm.currency||'OMR'} ${(pm.amount||0).toLocaleString()}`),esc((pm.status||'').replace(/_/g,' ')),esc(pm.planned_date||'—'),esc(pm.released_at?.slice(0,10)||'—'),esc(pm.finance_user_name||'—')];
      const fColors=['#111','#059669',statusColor,'#111','#111','#111'];
      cols.forEach((col,ci)=>{
        const txt=String(vals[ci]||'').slice(0,Math.floor(col.w/6.5));
        svg+=`<text x="${x+6}" y="${y+20}" font-family="Arial" font-size="10" fill="${fColors[ci]}">${txt}</text>`;
        x+=col.w;
      });
      y+=rowH;
    });
    svg+='</svg>';
    const blob=new Blob([svg],{type:'image/svg+xml'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a'); a.href=url;
    a.download=`Payment_Milestones_${space?.name||'export'}_${new Date().toISOString().slice(0,10)}.svg`;
    a.click(); URL.revokeObjectURL(url);
  }

  if (isGuest || (!isProjectPM && user?.role !== 'superadmin')) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'60vh',gap:'16px'}}>
      <div style={{fontSize:'48px'}}>🔒</div>
      <div style={{fontSize:'18px',fontWeight:'700',color:'var(--text)'}}>Access Restricted</div>
      <div style={{fontSize:'14px',color:'var(--muted)',textAlign:'center',maxWidth:'400px'}}>
        Payment Milestones are only visible to designated Project Managers.
        Contact your Project Manager to get access.
      </div>
    </div>
  );

  if (loading) return (
    <div className="page-enter" style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:'10px',color:'var(--muted)'}}>
      <div style={{width:16,height:16,border:'2px solid var(--border)',borderTopColor:'var(--brand)',borderRadius:'50%',animation:'spin .7s linear infinite'}}/>
      Loading payment milestones…
    </div>
  );

  return (
    <div style={{flex:1,overflowY:'auto',overflowX:'hidden',background:'var(--bg)',minHeight:0}}>

      {toast&&(
        <div style={{position:'fixed',top:16,right:20,zIndex:9999,padding:'10px 18px',borderRadius:'8px',
          background:toast.type==='error'?'#DC2626':'#16A34A',color:'#fff',fontWeight:'600',fontSize:'13px',
          boxShadow:'0 4px 16px rgba(0,0,0,0.2)'}}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{padding:'14px 24px',borderBottom:'1px solid var(--border)',background:'var(--surface)',display:'flex',alignItems:'center',gap:'16px'}}>
        <div>
          <h1 style={{fontSize:'17px',fontWeight:'700',color:'var(--text)',margin:0}}>
            💰 Payment Milestones
            {space&&<span style={{color:'var(--muted)',fontWeight:'400',fontSize:'14px'}}> — {space.name}</span>}
          </h1>
          <p style={{fontSize:'11px',color:'var(--muted)',margin:'2px 0 0'}}>Track contract payments, budgets and release approvals</p>
        </div>
        <div style={{marginLeft:'auto',display:'flex',gap:'8px',alignItems:'center'}}>
          <button className="btn btn-sm" onClick={exportPaymentCSV}>📊 Export Excel</button>
          <button className="btn btn-sm" onClick={exportPaymentSVG}>📄 Export SVG</button>
          <button className="btn btn-sm" onClick={exportPaymentPrint}>🖨️ Print / PDF</button>
          <button className="btn btn-primary" onClick={openNew}>+ New Milestone</button>
        </div>
      </div>

      <div style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:'20px'}}>

        {/* Summary KPIs */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'12px'}}>
          {[
            {label:'Total Contract Value',
             value: isMultiCurrency
               ? currencies.map(c=>`${c} ${byCurrency[c].total.toLocaleString('en-GB',{minimumFractionDigits:2})}`).join(' · ')
               : `${currency} ${(parseFloat(totalContract||0)).toLocaleString('en-GB',{minimumFractionDigits:2})}`,
             color:'#5B5BD6', sub: isMultiCurrency ? `${currencies.length} currencies` : null},
            {label:'Total Released',
             value: isMultiCurrency
               ? currencies.map(c=>`${c} ${byCurrency[c].released.toLocaleString('en-GB',{minimumFractionDigits:2})}`).join(' · ')
               : `${currency} ${(parseFloat(totalReleased||0)).toLocaleString('en-GB',{minimumFractionDigits:2})}`,
             color:'#16A34A', sub:null},
            {label:'Pending Release',
             value: isMultiCurrency
               ? currencies.map(c=>`${c} ${byCurrency[c].pending.toLocaleString('en-GB',{minimumFractionDigits:2})}`).join(' · ')
               : `${currency} ${(parseFloat(totalPending||0)).toLocaleString('en-GB',{minimumFractionDigits:2})}`,
             color:'#D97706', sub:null},
            {label:'Ready for Release',     value:readyCount,                                        color:'#059669', sub:`${readyCount} milestone${readyCount!==1?'s':''}`},
            {label:'Total Over-release',    value:totalOverRelease>0?`${currency} ${(parseFloat(totalOverRelease||0)).toLocaleString('en-GB',{minimumFractionDigits:2})}`:'—', color:totalOverRelease>0?'#B45309':'#8A93A6', sub:totalOverRelease>0?`Total released exceeds total contract`:null, warn:totalOverRelease>0},
          ].map(k=>(
            <div key={k.label} className="card" style={{padding:'16px 18px',border:k.warn?'1.5px solid rgba(180,83,9,0.35)':'none',background:k.warn?'rgba(180,83,9,0.04)':'var(--surface)'}}>
              <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:k.warn?'#92400E':'var(--muted2)',marginBottom:'6px',display:'flex',alignItems:'center',gap:'5px'}}>
                {k.warn&&<span>⚠️</span>}{k.label}
              </div>
              <div style={{
                fontSize: isMultiCurrency && typeof k.value==='string' && k.value.includes('·') ? '11px' : '20px',
                fontWeight:'800',color:k.color,
                letterSpacing: isMultiCurrency ? '0' : '-1px',
                lineHeight: isMultiCurrency ? '1.6' : '1.2',
                whiteSpace: isMultiCurrency ? 'normal' : 'nowrap',
                overflow: 'hidden'
              }}>{k.value}</div>
              {k.sub&&<div style={{fontSize:'10px',color:'var(--muted)',marginTop:'3px'}}>{k.sub}</div>}
            </div>
          ))}
        </div>

        {/* Budget vs Actual bar */}
        {totalContract > 0 && (
          <div className="card" style={{padding:'18px 20px'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
              <span style={{fontSize:'12px',fontWeight:'700',color:'var(--text)'}}>Budget vs Actual Release</span>
              <span style={{fontSize:'12px',color:'var(--muted)'}}>
                {isMultiCurrency
                  ? currencies.map(c=>`${c}: ${byCurrency[c].released.toLocaleString()} / ${byCurrency[c].total.toLocaleString()}`).join('  |  ')
                  : `${currency} ${(parseFloat(totalReleased||0)).toLocaleString()} of ${(parseFloat(totalContract||0)).toLocaleString()}`
                }
                {totalContract>0&&<span style={{fontWeight:'700',color:'#16A34A',marginLeft:'8px'}}>({Math.round((totalReleased/totalContract)*100)}%)</span>}
              </span>
            </div>
            <div style={{height:'12px',background:'var(--surface3)',borderRadius:'99px',overflow:'hidden',position:'relative'}}>
              <div style={{height:'100%',width:`${Math.min(100,(totalReleased/totalContract)*100)}%`,
                background:'linear-gradient(90deg,#16A34A,#22C55E)',borderRadius:'99px',transition:'width .5s'}}/>
            </div>
            <div style={{display:'flex',gap:'16px',marginTop:'8px',fontSize:'11px'}}>
              <span style={{display:'flex',alignItems:'center',gap:'4px'}}><span style={{width:8,height:8,borderRadius:'50%',background:'#16A34A',display:'inline-block'}}/>Released</span>
              <span style={{display:'flex',alignItems:'center',gap:'4px'}}><span style={{width:8,height:8,borderRadius:'50%',background:'var(--surface3)',display:'inline-block'}}/>Pending</span>
            </div>
          </div>
        )}

        {/* Milestones list */}
        {milestones.length === 0 ? (
          <div className="card" style={{padding:'48px',textAlign:'center',color:'var(--muted)'}}>
            <div style={{fontSize:'40px',marginBottom:'12px'}}>💳</div>
            <div style={{fontSize:'15px',fontWeight:'600',marginBottom:'6px'}}>No payment milestones yet</div>
            <div style={{fontSize:'12px',marginBottom:'20px'}}>Create your first milestone to start tracking contract payments</div>
            <button className="btn btn-primary" onClick={openNew}>+ New Milestone</button>
          </div>
        ) : (
          milestones.map(pm => {
            const sc = STATUS_CONFIG[pm.status] || STATUS_CONFIG.pending;
            const isExpanded = expanded[pm.id];
            const tasksDone = pm.tasks?.filter(t=>t.progress>=100).length||0;
            const tasksTotal = pm.tasks?.length||0;
            const allDone = tasksTotal>0 && tasksDone===tasksTotal;
            return (
              <div key={pm.id} className="card" style={{overflow:'hidden'}}>
                {/* Milestone header */}
                <div style={{padding:'16px 20px',display:'flex',alignItems:'flex-start',gap:'14px',cursor:'pointer'}}
                  onClick={()=>setExpanded(e=>({...e,[pm.id]:!e[pm.id]}))}>
                  <div style={{flex:1}}>
                    <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap',marginBottom:'6px'}}>
                      <span style={{fontSize:'15px',fontWeight:'700',color:'var(--text)'}}>{pm.name}</span>
                      <span style={{padding:'2px 10px',borderRadius:'99px',fontSize:'11px',fontWeight:'700',
                        background:sc.bg,color:sc.color}}>
                        {sc.label}
                      </span>
                      {pm.contract_reference&&(
                        <span style={{fontSize:'11px',color:'var(--muted)',background:'var(--surface2)',padding:'2px 8px',borderRadius:'4px',border:'1px solid var(--border)'}}>
                          Ref: {pm.contract_reference}
                        </span>
                      )}
                    </div>
                    {pm.description&&<p style={{fontSize:'12px',color:'var(--muted)',margin:'0 0 8px'}}>{pm.description}</p>}
                    <div style={{display:'flex',gap:'20px',flexWrap:'wrap',fontSize:'12px',color:'var(--muted)'}}>
                      <span>💰 Planned: <strong style={{color:'var(--text)',fontSize:'14px'}}>{pm.currency} {(parseFloat(pm.amount||0)).toLocaleString()}</strong></span>
                      {(()=>{
                        const totalReleased = (pm.releaseLog||[]).reduce((s,r)=>s+(r.amount||0),0);
                        if(totalReleased===0) return null;
                        const variance = totalReleased - pm.amount;
                        const isOver = variance > 0;
                        const isUnder = variance < 0;
                        return (
                          <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}>
                            <span style={{fontSize:'11px',color:'var(--muted)'}}>→ Released:</span>
                            <strong style={{color:isOver?'#B45309':isUnder?'#5B5BD6':'#16A34A',fontSize:'14px'}}>
                              {pm.currency} {(parseFloat(totalReleased||0)).toLocaleString()}
                            </strong>
                            {variance!==0&&(
                              <span style={{
                                padding:'2px 8px',borderRadius:'99px',fontSize:'10px',fontWeight:'800',
                                background:isOver?'rgba(180,83,9,0.1)':'rgba(91,91,214,0.1)',
                                color:isOver?'#B45309':'#5B5BD6',
                                border:`1px solid ${isOver?'rgba(180,83,9,0.3)':'rgba(91,91,214,0.3)'}`
                              }}>
                                {isOver?'▲ Over-release':' Under-release'} {pm.currency} {(parseFloat(Math.abs(variance)||0)).toLocaleString()}
                              </span>
                            )}
                          </span>
                        );
                      })()}
                      {pm.planned_date&&<span>📅 Target: <strong style={{color:'var(--text)'}}>{fmtDate(pm.planned_date)}</strong></span>}
                      {pm.finance_user_name&&(
                        <span style={{display:'flex',alignItems:'center',gap:'4px'}}>
                          👤 Finance: <strong style={{color:'var(--text)'}}>{pm.finance_user_name}</strong>
                        </span>
                      )}
                      <span>📋 Tasks: <strong style={{color:allDone?'#16A34A':'var(--text)'}}>{tasksDone}/{tasksTotal} done</strong></span>
                    </div>
                    {tasksTotal>0&&(
                      <div style={{marginTop:'8px',height:'5px',background:'var(--surface3)',borderRadius:'99px',overflow:'hidden',maxWidth:'300px'}}>
                        <div style={{height:'100%',width:`${(tasksDone/tasksTotal)*100}%`,
                          background:allDone?'#16A34A':'#5B5BD6',borderRadius:'99px',transition:'width .4s'}}/>
                      </div>
                    )}
                  </div>
                  <div style={{display:'flex',gap:'8px',alignItems:'center',flexShrink:0}}>
                    {pm.status==='ready_for_release'&&(
                      <button className="btn btn-primary" style={{fontSize:'12px',padding:'5px 12px',background:'#059669'}}
                        onClick={e=>{e.stopPropagation();setReleaseForm({reference_number:'',notes:'',amount:String(pm.amount)});setReleaseModal(pm);}}>
                        💰 Release Payment
                      </button>
                    )}
                    <button className="btn btn-sm" onClick={e=>{e.stopPropagation();openEdit(pm);}}>Edit</button>
                    <button className="btn btn-sm" style={{color:'#DC2626',border:'1px solid #DC262640'}}
                      onClick={e=>{e.stopPropagation();setDeleteId(pm.id);}}>Delete</button>
                    <span style={{fontSize:'12px',color:'var(--muted)',marginLeft:'4px'}}>{isExpanded?'▲':'▼'}</span>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded&&(
                  <div style={{borderTop:'1px solid var(--border)',background:'var(--surface2)'}}>

                    {/* Linked tasks */}
                    {pm.tasks?.length>0&&(
                      <div style={{padding:'14px 20px',borderBottom:'1px solid var(--border)'}}>
                        <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted2)',marginBottom:'10px'}}>
                          Linked Tasks ({pm.tasks.length})
                        </div>
                        <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                          {pm.tasks.map(t=>(
                            <div key={t.id} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 12px',
                              background:'var(--surface)',borderRadius:'6px',border:'1px solid var(--border)'}}>
                              <div style={{width:8,height:8,borderRadius:'50%',flexShrink:0,
                                background:t.progress>=100?'#16A34A':t.progress>=90?'#D97706':'#5B5BD6'}}/>
                              <div style={{flex:1}}>
                                <span style={{fontSize:'12px',fontWeight:'600',color:'var(--text)'}}>{t.name}</span>
                                <span style={{fontSize:'11px',color:'var(--muted)',marginLeft:'8px'}}>{t.phase_name} → {t.list_name}</span>
                              </div>
                              <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                                <div style={{width:'80px',height:'5px',background:'var(--surface3)',borderRadius:'99px',overflow:'hidden'}}>
                                  <div style={{height:'100%',width:`${t.progress||0}%`,
                                    background:t.progress>=100?'#16A34A':t.progress>=90?'#D97706':'#5B5BD6',borderRadius:'99px'}}/>
                                </div>
                                <span style={{fontSize:'12px',fontWeight:'700',
                                  color:t.progress>=100?'#16A34A':t.progress>=90?'#D97706':'var(--text)',minWidth:'35px',textAlign:'right'}}>
                                  {t.progress||0}%
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Trigger Timeline */}
                    {pm.triggers?.length>0&&(
                      <div style={{padding:'14px 20px',borderBottom:'1px solid var(--border)'}}>
                        <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted2)',marginBottom:'10px'}}>
                          Audit Timeline
                        </div>
                        <div style={{display:'flex',flexDirection:'column',gap:'0'}}>
                          {pm.triggers.map((tr,i)=>{
                            const TT = {
                              '100_percent':              { label:'Task reached 100%',                  color:'#16A34A' },
                              '90_percent':               { label:'Task reached 90%',                   color:'#D97706' },
                              'AUTO_RESOLVED':            { label:'Milestone auto-resolved (re-completed)', color:'#16A34A' },
                              'TASK_REOPENED':            { label:'Task re-opened — under review',       color:'#DC2626' },
                              'TASK_REOPENED_AFTER_RELEASE': { label:'Task re-opened after release',     color:'#DC2626' },
                            };
                            const meta = TT[tr.trigger_type] || { label:(tr.trigger_type||'Event').replace(/_/g,' '), color:'#6B7280' };
                            return (
                            <div key={tr.id} style={{display:'flex',gap:'12px',paddingBottom:'12px',position:'relative'}}>
                              {i<pm.triggers.length-1&&(
                                <div style={{position:'absolute',left:'7px',top:'16px',width:'2px',bottom:0,background:'var(--border)'}}/>
                              )}
                              <div style={{width:16,height:16,borderRadius:'50%',flexShrink:0,marginTop:'1px',
                                background:meta.color,
                                border:'2px solid var(--surface2)',zIndex:1}}/>
                              <div>
                                <div style={{fontSize:'12px',fontWeight:'600',color:'var(--text)'}}>
                                  {meta.label}
                                </div>
                                <div style={{fontSize:'11px',color:'var(--muted)',marginTop:'2px'}}>
                                  {fmtDateTime(tr.triggered_at)} · by {tr.triggered_by_name}
                                  {tr.email_sent?<span style={{color:'#16A34A',marginLeft:'8px'}}>✓ Email sent</span>:null}
                                  {tr.notification_sent?<span style={{color:'#5B5BD6',marginLeft:'6px'}}>✓ Notified</span>:null}
                                </div>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Release Log */}
                    {pm.releaseLog?.length>0&&(
                      <div style={{padding:'14px 20px'}}>
                        <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'.6px',color:'var(--muted2)',marginBottom:'10px'}}>
                          Payment Release Log
                        </div>
                        {pm.releaseLog.map((rl, rlIdx)=>{
                          const variance = rl.amount - pm.amount;
                          const isOver = variance > 0;
                          const isUnder = variance < 0;
                          const isExact = variance === 0;
                          const variancePct = pm.amount > 0 ? Math.abs(variance)/pm.amount*100 : 0;
                          const bgColor = isOver ? '#FEF3C7' : isUnder ? '#EFF6FF' : '#D1FAE5';
                          const borderColor = isOver ? '#FCD34D' : isUnder ? '#BFDBFE' : '#6EE7B7';
                          const textColor = isOver ? '#92400E' : isUnder ? '#1E40AF' : '#065F46';
                          const subColor = isOver ? '#B45309' : isUnder ? '#2563EB' : '#047857';
                          return (
                          <div key={rl.id} style={{display:'flex',gap:'12px',padding:'12px 14px',
                            background:bgColor,border:`1px solid ${borderColor}`,borderRadius:'8px',marginBottom:'8px'}}>
                            <span style={{fontSize:'20px',flexShrink:0}}>{isOver?'⚠️':isUnder?'📉':'✅'}</span>
                            <div style={{flex:1}}>
                              <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap',marginBottom:'4px'}}>
                                <div style={{fontSize:'14px',fontWeight:'800',color:textColor}}>
                                  {pm.currency} {(parseFloat(rl.amount||0)).toLocaleString()}
                                </div>
                                {!isExact&&(
                                  <div style={{
                                    padding:'3px 10px',borderRadius:'99px',fontSize:'11px',fontWeight:'800',
                                    background:isOver?'rgba(180,83,9,0.15)':'rgba(37,99,235,0.1)',
                                    color:isOver?'#92400E':'#1E40AF',
                                    border:`1.5px solid ${isOver?'rgba(180,83,9,0.4)':'rgba(37,99,235,0.3)'}`
                                  }}>
                                    {isOver ? '▲ Over-release' : '▼ Under-release'}: {pm.currency} {(parseFloat(Math.abs(variance)||0)).toLocaleString()}
                                    {variancePct>0&&<span style={{marginLeft:'4px',fontWeight:'700',opacity:.75}}>({variancePct.toFixed(1)}%)</span>}
                                  </div>
                                )}
                                {isExact&&<span style={{fontSize:'11px',color:'#047857',fontWeight:'700'}}>✓ Exact amount</span>}
                              </div>
                              <div style={{fontSize:'11px',color:subColor,marginBottom:'2px'}}>
                                <strong>Planned:</strong> {pm.currency} {(parseFloat(pm.amount||0)).toLocaleString()}
                                {!isExact&&<span style={{marginLeft:'8px'}}><strong>Variance:</strong> {isOver?'+':''}{(parseFloat(variance||0)).toLocaleString()} {pm.currency}</span>}
                              </div>
                              <div style={{fontSize:'11px',color:subColor,marginTop:'3px'}}>
                                {fmtDateTime(rl.released_at)} · Authorized by {rl.released_by_name}
                                {rl.reference_number&&<span style={{marginLeft:'8px',fontWeight:'700'}}>Ref: {rl.reference_number}</span>}
                              </div>
                              {rl.notes&&<div style={{fontSize:'11px',color:textColor,marginTop:'4px',fontStyle:'italic'}}>"{rl.notes}"</div>}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Empty triggers state */}
                    {pm.triggers?.length===0&&pm.releaseLog?.length===0&&(
                      <div style={{padding:'20px',textAlign:'center',color:'var(--muted)',fontSize:'12px'}}>
                        No activity yet — audit trail will appear here as tasks progress
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal&&(
        <div className="modal-overlay">
          <div className="modal" style={{width:'560px',maxHeight:'85vh',overflowY:'auto'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'20px'}}>
              <h3 style={{fontSize:'16px',fontWeight:'700',margin:0}}>{editPm?'Edit Payment Milestone':'New Payment Milestone'}</h3>
              <button className="btn btn-sm" onClick={()=>setShowModal(false)}>✕</button>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
              <div className="form-group" style={{gridColumn:'1/3'}}>
                <label className="form-label">Milestone Name <span style={{color:'#DC2626'}}>*</span></label>
                <input className="input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Phase 1 Delivery Payment"/>
              </div>
              <div className="form-group" style={{gridColumn:'1/3'}}>
                <label className="form-label">Description</label>
                <textarea className="input" rows={2} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} style={{resize:'vertical'}} placeholder="Optional description…"/>
              </div>
              <div className="form-group">
                <label className="form-label">Amount <span style={{color:'#DC2626'}}>*</span></label>
                <input className="input" type="number" min="0" step="0.01" value={form.amount}
                  onChange={e=>setForm(f=>({...f,amount:e.target.value}))} placeholder="e.g. 15000"/>
              </div>
              <div className="form-group">
                <label className="form-label">Currency</label>
                <select className="select" value={form.currency} onChange={e=>setForm(f=>({...f,currency:e.target.value}))}>
                  {['OMR','USD','EUR','GBP','AED','SAR'].map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Target Payment Date</label>
                <input className="input" type="date" value={form.planned_date} onChange={e=>setForm(f=>({...f,planned_date:e.target.value}))}/>
              </div>
              <div className="form-group">
                <label className="form-label">Contract Reference</label>
                <input className="input" value={form.contract_reference} onChange={e=>setForm(f=>({...f,contract_reference:e.target.value}))} placeholder="e.g. SCT-2026-001"/>
              </div>
              <div className="form-group" style={{gridColumn:'1/3'}}>
                <label className="form-label">Finance Responsible <span style={{fontSize:'10px',fontWeight:'400',color:'var(--muted)'}}>select one or more</span></label>
                <FinanceMultiSelect
                  users={allUsers}
                  selected={Array.isArray(form.finance_user_ids)?form.finance_user_ids:(form.finance_user_id?[form.finance_user_id]:[])}
                  onChange={ids=>setForm(f=>({...f,finance_user_ids:ids,finance_user_id:ids[0]||''}))}
                />
              </div>
            </div>

            {/* Task linking */}
            <div className="form-group" style={{marginTop:'4px'}}>
              <label className="form-label">Link to Tasks <span style={{fontSize:'10px',fontWeight:'400',color:'var(--muted)'}}>alerts trigger when these reach 90% and 100%</span></label>
              <input className="input" value={form.taskSearch||''} onChange={e=>setForm(f=>({...f,taskSearch:e.target.value}))}
                placeholder="🔍 Search tasks…" style={{marginBottom:'6px',fontSize:'12px'}}/>
              <div style={{border:'1px solid var(--border)',borderRadius:'var(--radius)',maxHeight:'200px',overflowY:'auto',padding:'8px'}}>
                {allTasks.length===0&&<div style={{fontSize:'12px',color:'var(--muted)',padding:'8px'}}>No tasks found in this project</div>}
                {phases.map(phase=>(
                  <div key={phase}>
                    <div style={{fontSize:'10px',fontWeight:'700',textTransform:'uppercase',color:'var(--muted2)',padding:'4px 6px',letterSpacing:'.5px'}}>{phase}</div>
                    {allTasks.filter(t=>(t.phase_name||'Other')===phase&&(!form.taskSearch||t.name.toLowerCase().includes(form.taskSearch.toLowerCase())||t.list_name?.toLowerCase().includes(form.taskSearch.toLowerCase()))).map(t=>(
                      <label key={t.id} style={{display:'flex',alignItems:'center',gap:'8px',padding:'5px 6px',borderRadius:'5px',cursor:'pointer',
                        background:form.task_ids.includes(t.id)?'rgba(91,91,214,0.08)':'transparent'}}>
                        <input type="checkbox" checked={form.task_ids.includes(t.id)} onChange={()=>toggleTask(t.id)}/>
                        <span style={{fontSize:'12px',flex:1}}>{t.name}</span>
                        <span style={{fontSize:'11px',color:'var(--muted)'}}>{t.list_name}</span>
                        <span style={{fontSize:'11px',fontWeight:'600',color:t.progress>=100?'#16A34A':'#5B5BD6'}}>{t.progress||0}%</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              {form.task_ids.length>0&&(
                <div style={{fontSize:'11px',color:'#5B5BD6',marginTop:'4px',fontWeight:'600'}}>
                  {form.task_ids.length} task{form.task_ids.length!==1?'s':''} selected
                </div>
              )}
            </div>

            <div style={{display:'flex',gap:'8px',justifyContent:'flex-end',paddingTop:'14px',borderTop:'1px solid var(--border)',marginTop:'8px'}}>
              <button className="btn" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveMilestone}>{editPm?'Save Changes':'Create Milestone'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Release Payment Modal */}
      {releaseModal&&(()=>{
        const contractAmt = releaseModal.amount || 0;
        const alreadyReleased = (releaseModal.releaseLog||[]).reduce((s,l)=>s+(parseFloat(l.amount)||0),0);
        const thisRelease = parseFloat(releaseForm.amount)||contractAmt;
        const totalAfter = alreadyReleased + thisRelease;
        const remaining = contractAmt - totalAfter;
        const isOver = totalAfter > contractAmt;
        const isFull = Math.abs(remaining) < 0.01;
        const isPartial = remaining > 0.01;
        const plannedDate = releaseModal.planned_date;
        const today = new Date().toISOString().slice(0,10);
        const daysDiff = plannedDate ? Math.round((new Date(today)-new Date(plannedDate))/(1000*60*60*24)) : null;
        const cur = releaseModal.currency||'OMR';
        return (
          <div className="modal-overlay">
            <div className="modal" style={{width:'500px',maxHeight:'90vh',overflowY:'auto'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'16px'}}>
                <h3 style={{fontSize:'16px',fontWeight:'700',margin:0}}>💰 Release Payment</h3>
                <button className="btn btn-sm" onClick={()=>setReleaseModal(null)}>✕</button>
              </div>

              {/* Milestone summary */}
              <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:'10px',padding:'14px 16px',marginBottom:'16px'}}>
                <div style={{fontSize:'13px',fontWeight:'700',color:'#065F46',marginBottom:'6px'}}>{releaseModal.name}</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'8px',fontSize:'12px'}}>
                  <div><div style={{color:'#6B7280',marginBottom:'2px'}}>Contract Amount</div><div style={{fontWeight:'800',color:'#065F46',fontSize:'14px'}}>{cur} {(parseFloat(contractAmt||0)).toLocaleString()}</div></div>
                  <div><div style={{color:'#6B7280',marginBottom:'2px'}}>Already Released</div><div style={{fontWeight:'700',color:alreadyReleased>0?'#059669':'#9CA3AF',fontSize:'14px'}}>{cur} {(parseFloat(alreadyReleased||0)).toLocaleString()}</div></div>
                  <div><div style={{color:'#6B7280',marginBottom:'2px'}}>Remaining</div><div style={{fontWeight:'700',color:alreadyReleased>0?'#D97706':'#9CA3AF',fontSize:'14px'}}>{cur} {Math.max(0,contractAmt-alreadyReleased).toLocaleString()}</div></div>
                </div>
                {plannedDate&&<div style={{marginTop:'8px',fontSize:'11px',color:daysDiff>0?'#D97706':daysDiff<0?'#059669':'#6B7280',fontWeight:'600'}}>
                  📅 Planned: {plannedDate} · Actual: {today} · {daysDiff===0?'On time':daysDiff>0?`${daysDiff} day(s) late`:` ${Math.abs(daysDiff)} day(s) early`}
                </div>}
              </div>

              {/* Release amount */}
              <div className="form-group">
                <label className="form-label">Release Amount <span style={{color:'#DC2626'}}>*</span></label>
                <input className="input" type="number" min="0" step="0.01" value={releaseForm.amount}
                  onChange={e=>setReleaseForm(f=>({...f,amount:e.target.value}))}
                  placeholder={`Remaining: ${cur} ${Math.max(0,contractAmt-alreadyReleased).toLocaleString()}`}/>
                {/* Live calculation */}
                <div style={{marginTop:'8px',padding:'10px 14px',borderRadius:'8px',background:isOver?'#FEF2F2':isFull?'#F0FDF4':'#FFF7ED',border:`1px solid ${isOver?'#FCA5A5':isFull?'#BBF7D0':'#FED7AA'}`,fontSize:'12px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}>
                    <span style={{color:'#6B7280'}}>This release:</span>
                    <strong style={{color:isOver?'#DC2626':isFull?'#059669':'#D97706'}}>{cur} {(parseFloat(thisRelease||0)).toLocaleString()}</strong>
                  </div>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}>
                    <span style={{color:'#6B7280'}}>Total released after:</span>
                    <strong style={{color:isOver?'#DC2626':isFull?'#059669':'#374151'}}>{cur} {(parseFloat(totalAfter||0)).toLocaleString()} / {cur} {(parseFloat(contractAmt||0)).toLocaleString()} ({Math.round(totalAfter/contractAmt*100)}%)</strong>
                  </div>
                  {isOver&&<div style={{color:'#DC2626',fontWeight:'700',marginTop:'4px'}}>⚠ Over-release by {cur} {(totalAfter-contractAmt).toLocaleString()}</div>}
                  {isFull&&<div style={{color:'#059669',fontWeight:'700',marginTop:'4px'}}>✅ Full payment — this milestone will be closed</div>}
                  {isPartial&&<div style={{color:'#D97706',fontWeight:'700',marginTop:'4px'}}>⏳ Partial — {cur} {(parseFloat(remaining||0)).toLocaleString()} remaining after this release</div>}
                </div>
              </div>

              {/* Partial payment option */}
              {isPartial&&(
                <div style={{background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:'8px',padding:'12px 14px',marginBottom:'12px'}}>
                  <div style={{fontSize:'12px',fontWeight:'700',color:'#92400E',marginBottom:'8px'}}>⏳ Partial Payment — what should happen to the remaining {cur} {(parseFloat(remaining||0)).toLocaleString()}?</div>
                  <label style={{display:'flex',alignItems:'flex-start',gap:'8px',cursor:'pointer',marginBottom:'6px'}}>
                    <input type="radio" name="partialAction" checked={releaseForm.keepOpen!==false} onChange={()=>setReleaseForm(f=>({...f,keepOpen:true}))} style={{marginTop:'2px'}}/>
                    <div><div style={{fontSize:'12px',fontWeight:'600',color:'#374151'}}>Keep open for future payment</div><div style={{fontSize:'11px',color:'#6B7280'}}>Milestone stays active; remaining balance can be released later</div></div>
                  </label>
                  <label style={{display:'flex',alignItems:'flex-start',gap:'8px',cursor:'pointer'}}>
                    <input type="radio" name="partialAction" checked={releaseForm.keepOpen===false} onChange={()=>setReleaseForm(f=>({...f,keepOpen:false}))} style={{marginTop:'2px'}}/>
                    <div><div style={{fontSize:'12px',fontWeight:'600',color:'#374151'}}>Close this milestone</div><div style={{fontSize:'11px',color:'#6B7280'}}>Milestone will be marked as released with partial amount — no further adjustments</div></div>
                  </label>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Reference Number</label>
                <input className="input" value={releaseForm.reference_number}
                  onChange={e=>setReleaseForm(f=>({...f,reference_number:e.target.value}))}
                  placeholder="e.g. INV-2026-001"/>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="input" rows={2} value={releaseForm.notes}
                  onChange={e=>setReleaseForm(f=>({...f,notes:e.target.value}))}
                  style={{resize:'vertical'}} placeholder="Any additional notes…"/>
              </div>

              <div style={{display:'flex',gap:'8px',justifyContent:'flex-end',paddingTop:'14px',borderTop:'1px solid var(--border)'}}>
                <button className="btn" onClick={()=>setReleaseModal(null)}>Cancel</button>
                <button className="btn btn-primary" style={{background:isOver?'#DC2626':'#059669'}}
                  onClick={()=>releasePayment(isPartial&&releaseForm.keepOpen!==false)}>
                  {isOver?'⚠ Confirm Over-Release':isFull?'✅ Confirm Full Release':'💰 Confirm Partial Release'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete Confirm */}
      {deleteId&&(
        <div className="modal-overlay">
          <div className="modal" style={{width:'380px',textAlign:'center'}}>
            <div style={{fontSize:'32px',marginBottom:'12px'}}>🗑️</div>
            <h3 style={{fontSize:'15px',fontWeight:'700',marginBottom:'8px'}}>Delete Payment Milestone?</h3>
            <p style={{fontSize:'13px',color:'var(--muted)',marginBottom:'20px'}}>This will also delete all triggers and release logs. This cannot be undone.</p>
            <div style={{display:'flex',gap:'8px',justifyContent:'center'}}>
              <button className="btn" style={{minWidth:'90px'}} onClick={()=>setDeleteId(null)}>Cancel</button>
              <button className="btn" style={{minWidth:'90px',background:'#DC2626',color:'#fff',border:'none'}} onClick={deleteMilestone}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
